import Phaser from 'phaser';
import { ItemRarity, TILE_SIZE, itemType, type GroundItemsPacket } from '@game/protocol';

import { itemTextureKey } from '../art/items.ts';
import { rarityHaloKey } from '../art/rarity.ts';

/**
 * Itens no chão.
 *
 * O servidor manda o estado completo da Área de Interesse sempre que algo muda, então a
 * camada é reconstruída inteira a cada pacote. É a decisão certa aqui: item no chão muda
 * raramente — um abate, um descarte — e manter um diff por tile custaria mais código do
 * que o redesenho economiza.
 *
 * Só a pilha do **topo** de cada tile é desenhada. Empilhar dez ícones num quadrado de
 * 32 px viraria um borrão; o topo é também o que o clique pega, então o que se vê é o
 * que se pega.
 */
/**
 * A pilha do topo de um tile, com o índice que endereça ela no `MoveItem`.
 *
 * É o que o arraste precisa saber e o desenho já sabia: o topo é o que se vê, e o `slot` é o
 * que diz ao servidor **qual** das pilhas daquele tile foi arrastada. Sem o índice, o gesto
 * teria que supor que o item visível é o primeiro da lista — que é verdade só em tile com uma
 * pilha só, e mentira exatamente onde há loot empilhado.
 */
export interface GroundTopStack {
  typeId: number;
  count: number;
  /** Índice da pilha dentro do tile, do fundo para o topo. É o `fromSlot` do `MoveItem`. */
  slot: number;
}

/** Chave de tile com andar. `z` cabe num byte e `x`/`y` são `u16` no protocolo. */
function keyOf(x: number, y: number, z: number): number {
  return (z * 0x10000 + y) * 0x10000 + x;
}

export class GroundItemLayer {
  private readonly scene: Phaser.Scene;
  private readonly sprites: Phaser.GameObjects.Sprite[] = [];
  private readonly counters: Phaser.GameObjects.Text[] = [];

  /**
   * O que há no topo de cada tile, para quem precisa **responder** e não desenhar.
   *
   * Alimentado pelo mesmo laço que desenha, e não por uma segunda passada sobre o pacote:
   * duas leituras da mesma lista são duas verdades livres para divergir, e a que o jogador
   * arrasta tem de ser a que ele está vendo.
   */
  private readonly index = new Map<number, GroundTopStack>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** A pilha visível neste tile, ou `undefined` se não há nada ali. */
  topAt(x: number, y: number, z: number): GroundTopStack | undefined {
    return this.index.get(keyOf(x, y, z));
  }

  update(packet: GroundItemsPacket): void {
    this.clear();

    for (const tile of packet.tiles) {
      const top = tile.items.at(-1);
      if (!top) continue;

      this.index.set(keyOf(tile.x, tile.y, tile.z), {
        typeId: top.typeId,
        count: top.count,
        slot: tile.items.length - 1,
      });

      const key = itemTextureKey(top.typeId);
      if (!this.scene.textures.exists(key)) continue;

      const x = tile.x * TILE_SIZE + TILE_SIZE / 2;
      const y = tile.y * TILE_SIZE + TILE_SIZE / 2;

      const sprite = this.scene.add.sprite(x, y, key);
      // Abaixo das criaturas, acima do chão: um item sob os pés de alguém precisa
      // continuar visível pela borda, mas não pode cobrir quem está em cima dele.
      sprite.setDepth(tile.y * 10 - 5);
      sprite.setScale(0.75);
      this.sprites.push(sprite);

      this.shine(sprite, itemType(top.typeId)?.rarity, x, y, tile.y);

      // O número é o da pilha **desenhada**, e o `+` diz que há mais coisa embaixo.
      //
      // Ele somava as contagens de **todas** as pilhas do tile enquanto o ícone era o de uma
      // só, e o resultado era um par que não descrevia nada: cem moedas sob três flechas
      // apareciam como o ícone da flecha com "103" ao lado. O jogador lia "103 flechas",
      // clicava, e recebia três — e a explicação ("era a soma de coisas diferentes") não está
      // disponível para quem está olhando a tela.
      //
      // O `+` é o que a soma tentava dizer e dizia errado: existe mais aqui do que o ícone
      // mostra. Um corpo com quatro coisas dentro não conta como "mais aqui" — o que ele
      // guarda é dele, e sai por um clique só.
      const label =
        (top.count > 1 ? String(top.count) : '') + (tile.items.length > 1 ? '+' : '');
      if (label === '') continue;

      const counter = this.scene.add.text(
        tile.x * TILE_SIZE + TILE_SIZE - 4,
        tile.y * TILE_SIZE + TILE_SIZE - 4,
        label,
        {
          fontFamily: 'ui-monospace, monospace',
          fontSize: '9px',
          color: '#f4f7fa',
          stroke: '#0b0f14',
          strokeThickness: 3,
        },
      );
      counter.setOrigin(1, 1);
      counter.setDepth(tile.y * 10 - 4);
      this.counters.push(counter);
    }
  }

  /**
   * O brilho do item lendário e do celestial caído no chão (PRD §15.11).
   *
   * No inventário a raridade tem a borda colorida para dizer o que é; no chão não há borda
   * nenhuma — o item é um ícone de 24 px sobre grama, e uma peça de faixa 8 largada num
   * despojo é indistinguível de uma flecha. O halo pulsante é o que resolve, e ele tem de ser
   * visto **de longe**: quem passa correndo por um tile não lê ícone, lê luz.
   *
   * O halo é uma textura à parte, e não o brilho estático que já vai dentro do ícone, porque
   * é ele que o `tween` faz respirar — o desenho do item precisa continuar nítido enquanto a
   * luz em volta cresce e diminui. E o item sobe e desce junto: um objeto que flutua é o
   * sinal que todo jogador de RPG já sabe ler sem que ninguém explique.
   */
  private shine(
    sprite: Phaser.GameObjects.Sprite,
    rarity: ItemRarity | undefined,
    x: number,
    y: number,
    tileY: number,
  ): void {
    if (rarity === undefined || rarity < ItemRarity.Legendary) return;

    const key = rarityHaloKey(rarity);
    if (!this.scene.textures.exists(key)) return;

    const celestial = rarity >= ItemRarity.Celestial;
    const period = celestial ? 900 : 1400;

    const halo = this.scene.add.sprite(x, y, key);
    // Atrás do ícone, e somando luz em vez de cobrindo: o halo tem de clarear a grama sob o
    // item, não pintar um disco por cima dela.
    halo.setDepth(tileY * 10 - 6);
    halo.setBlendMode(Phaser.BlendModes.ADD);
    halo.setAlpha(celestial ? 0.5 : 0.35);
    this.sprites.push(halo);

    this.scene.tweens.add({
      targets: halo,
      alpha: celestial ? 0.95 : 0.7,
      scale: { from: 0.7, to: 1.05 },
      duration: period,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.scene.tweens.add({
      targets: sprite,
      y: y - 3,
      duration: period * 1.5,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private clear(): void {
    for (const sprite of this.sprites) {
      // Matar o tween antes de destruir o alvo: a camada é reconstruída inteira a cada
      // pacote, e um tween sobrevivente continuaria escrevendo `alpha` num objeto morto —
      // que é como um item recolhido do chão deixaria o console cheio de erro por sessão.
      this.scene.tweens.killTweensOf(sprite);
      sprite.destroy();
    }
    for (const counter of this.counters) counter.destroy();
    this.sprites.length = 0;
    this.counters.length = 0;
    this.index.clear();
  }
}
